import React, { useState } from 'react';
import { Stack, Chip, Box, Avatar, Typography, Collapse } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  CollectionsBookmark as SessionsIcon,
  Tab as TabIcon,
  FolderSpecial as ProjectIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const chipSx = { height: 28, fontSize: '0.78rem', cursor: 'pointer' };

const UserRow = ({ user }) => (
  <Box
    component={Link}
    to={`/u/${user.username}`}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.75,
      px: 1,
      borderRadius: 1,
      textDecoration: 'none',
      color: 'inherit',
      '&:hover': { bgcolor: 'action.hover' }
    }}
  >
    <Avatar
      src={user.profilePictureUrl}
      sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
    >
      {(user.displayName || user.username)?.[0]?.toUpperCase()}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" fontWeight={500} noWrap>
        {user.displayName || user.username}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        @{user.username}
      </Typography>
    </Box>
  </Box>
);

const ProfileStats = ({ stats, profile }) => {
  const [expandFollowers, setExpandFollowers] = useState(false);
  const [expandFollowing, setExpandFollowing] = useState(false);

  if (!stats && !profile) return null;

  const followerCount = profile?.followerCount || 0;
  const followingCount = profile?.followingCount || 0;
  const followers = profile?.followers || [];
  const following = profile?.following || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Chip
          icon={<PeopleIcon sx={{ fontSize: '15px !important' }} />}
          label={`${followerCount} Follower${followerCount !== 1 ? 's' : ''}`}
          variant={expandFollowers ? 'filled' : 'outlined'}
          size="small"
          sx={chipSx}
          onClick={() => {
            setExpandFollowers(prev => !prev);
            if (!expandFollowers) setExpandFollowing(false);
          }}
          deleteIcon={expandFollowers ? <ExpandLessIcon sx={{ fontSize: '16px !important' }} /> : <ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
          onDelete={() => {
            setExpandFollowers(prev => !prev);
            if (!expandFollowers) setExpandFollowing(false);
          }}
        />
        <Chip
          icon={<PeopleIcon sx={{ fontSize: '15px !important' }} />}
          label={`${followingCount} Following`}
          variant={expandFollowing ? 'filled' : 'outlined'}
          size="small"
          sx={chipSx}
          onClick={() => {
            setExpandFollowing(prev => !prev);
            if (!expandFollowing) setExpandFollowers(false);
          }}
          deleteIcon={expandFollowing ? <ExpandLessIcon sx={{ fontSize: '16px !important' }} /> : <ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
          onDelete={() => {
            setExpandFollowing(prev => !prev);
            if (!expandFollowing) setExpandFollowers(false);
          }}
        />
        {stats?.totalSessions > 0 && (
          <Chip
            icon={<SessionsIcon sx={{ fontSize: '15px !important' }} />}
            label={`${stats.totalSessions} Session${stats.totalSessions !== 1 ? 's' : ''}`}
            variant="outlined"
            size="small"
            sx={{ ...chipSx, cursor: 'default' }}
          />
        )}
        {stats?.totalTabs > 0 && (
          <Chip
            icon={<TabIcon sx={{ fontSize: '15px !important' }} />}
            label={`${stats.totalTabs} Tab${stats.totalTabs !== 1 ? 's' : ''}`}
            variant="outlined"
            size="small"
            sx={{ ...chipSx, cursor: 'default' }}
          />
        )}
        {stats?.totalProjects > 0 && (
          <Chip
            icon={<ProjectIcon sx={{ fontSize: '15px !important' }} />}
            label={`${stats.totalProjects} Project${stats.totalProjects !== 1 ? 's' : ''}`}
            variant="outlined"
            size="small"
            sx={{ ...chipSx, cursor: 'default' }}
          />
        )}
      </Stack>

      {/* Expandable followers list */}
      <Collapse in={expandFollowers} timeout="auto">
        <Box sx={{ mt: 1.5, maxWidth: 360, mx: 'auto' }}>
          {followers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
              No followers yet
            </Typography>
          ) : (
            <>
              {followers.map(user => (
                <UserRow key={user.username} user={user} />
              ))}
              {followerCount > 10 && (
                <Box
                  component={Link}
                  to={`/u/${profile.username}?viewFollowers=1`}
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    py: 0.75,
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  View all {followerCount} &rarr;
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>

      {/* Expandable following list */}
      <Collapse in={expandFollowing} timeout="auto">
        <Box sx={{ mt: 1.5, maxWidth: 360, mx: 'auto' }}>
          {following.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
              Not following anyone yet
            </Typography>
          ) : (
            <>
              {following.map(user => (
                <UserRow key={user.username} user={user} />
              ))}
              {followingCount > 10 && (
                <Box
                  component={Link}
                  to={`/u/${profile.username}?viewFollowing=1`}
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    py: 0.75,
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  View all {followingCount} &rarr;
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProfileStats;
